/**
 * Extracts duration from an audio/video file using the browser's native
 * Audio/Video element. Falls back gracefully if the browser can't read
 * the format (e.g., .dss, .qcp) — FFmpeg fills the authoritative duration later.
 *
 * @param {File} file - The file to extract duration from
 * @returns {Promise<number|null>} Duration in seconds, or null if extraction fails
 */
export async function extractDuration(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);

    const isVideo = file.type?.startsWith("video") ||
      /\.(mov|mp4|flv|mpeg|mpg)$/i.test(file.name);

    const el = isVideo
      ? document.createElement("video")
      : new Audio();

    el.preload = "metadata";

    // Set a timeout in case loadedmetadata never fires
    const timeout = setTimeout(() => {
      URL.revokeObjectURL(url);
      el.src = "";
      resolve(null);
    }, 10000);

    el.onloadedmetadata = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(url);
      const duration = isFinite(el.duration) ? el.duration : null;
      el.src = "";
      resolve(duration);
    };

    el.onerror = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(url);
      el.src = "";
      resolve(null);
    };

    el.src = url;
  });
}
